import { EditorView, Decoration, type DecorationSet, ViewPlugin, type ViewUpdate, WidgetType } from '@codemirror/view'
import { StateField, StateEffect, Prec } from '@codemirror/state'
import { keymap } from '@codemirror/view'
import { apiUrl, authFetch } from '../../lib/api'

// Effects
const setGhost = StateEffect.define<string>()
const clearGhost = StateEffect.define<null>()

class GhostWidget extends WidgetType {
  constructor(readonly text: string) { super() }
  eq(other: GhostWidget) { return this.text === other.text }
  toDOM() {
    const span = document.createElement('span')
    span.className = 'cm-ghost-text'
    span.textContent = this.text
    span.style.cssText = 'color:#555;font-style:italic;pointer-events:none;opacity:0.5;'
    return span
  }
}

// State field: stores both the decoration AND the text string
const ghostField = StateField.define<{ deco: DecorationSet; text: string }>({
  create: () => ({ deco: Decoration.none, text: '' }),
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setGhost) && e.value) {
        const pos = tr.state.selection.main.head
        const widget = Decoration.widget({ widget: new GhostWidget(e.value), side: 1 })
        return { deco: Decoration.set([widget.range(pos)]), text: e.value }
      }
      if (e.is(clearGhost)) return { deco: Decoration.none, text: '' }
    }
    // Clear on any doc change or cursor move
    if (tr.docChanged || tr.selection) return { deco: Decoration.none, text: '' }
    return value
  },
  provide: (f) => EditorView.decorations.from(f, (val) => val.deco),
})

// Plugin: debounced fetch
const ghostPlugin = ViewPlugin.fromClass(class {
  timer: ReturnType<typeof setTimeout> | null = null
  abortCtrl: AbortController | null = null

  update(update: ViewUpdate) {
    if (!update.docChanged) return

    // Clear pending
    if (this.timer) clearTimeout(this.timer)
    if (this.abortCtrl) { this.abortCtrl.abort(); this.abortCtrl = null }

    this.timer = setTimeout(() => {
      const view = update.view
      const state = view.state
      const pos = state.selection.main.head
      const doc = state.doc.toString()
      const prefix = doc.slice(0, pos)
      const suffix = doc.slice(pos)

      if (prefix.trim().length < 8) return

      this.abortCtrl = new AbortController()
      authFetch(apiUrl('/api/chat/complete'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: this.abortCtrl.signal,
        body: JSON.stringify({ prefix, suffix, language: 'typescript' }),
      })
        .then((r) => r.json())
        .then((data) => {
          const text = data.completion?.trim()
          if (!text) return
          // Only show if cursor hasn't moved
          if (view.state.selection.main.head !== pos) return
          if (view.state.doc.toString() !== doc) return
          view.dispatch({ effects: setGhost.of(text) })
        })
        .catch(() => {})
    }, 800)
  }

  destroy() {
    if (this.timer) clearTimeout(this.timer)
    if (this.abortCtrl) this.abortCtrl.abort()
  }
})

// Highest-priority keymap so Tab beats indentWithTab when ghost is active
const ghostKeymap = Prec.highest(keymap.of([
  {
    key: 'Tab',
    run: (view) => {
      const { text } = view.state.field(ghostField)
      if (!text) return false
      const pos = view.state.selection.main.head
      view.dispatch({
        changes: { from: pos, insert: text },
        selection: { anchor: pos + text.length },
        effects: clearGhost.of(null),
      })
      return true
    },
  },
  {
    key: 'Escape',
    run: (view) => {
      const { text } = view.state.field(ghostField)
      if (!text) return false
      view.dispatch({ effects: clearGhost.of(null) })
      return true
    },
  },
]))

export function ghostTextExtension() {
  return [ghostField, ghostPlugin, ghostKeymap]
}
